import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, take, tap } from 'rxjs/operators';
import { locationToProviderContact } from '../contacts/callback-location.util';
import { ContactsViewModel } from '../contacts/contacts.view-model';
import { LocationProvider } from '../contacts/patient.model';
import { Messages } from '../shared/messages';
import { CollectionUtils } from '../shared/utils/collection-utils';
import { ContactService } from './contact.service';
import { FHIRServiceInjectionToken } from './fhir-service.providers';
import { Call } from './model/call';
import { CallSequence } from './model/call-sequence';
import { CallState } from './model/call-state';
import { CallStatesResponse } from './model/call-states';
import { ContactInformation } from './model/contact-information';
import { Message } from './model/message';
import { NotificationCancelResponse } from './model/notification-cancel';
import { NotificationRequest } from './model/notification-request';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationViewModel {
  private fhirService = inject(FHIRServiceInjectionToken);
  private _loadingPatientContacts$ = new BehaviorSubject<boolean | null>(null);
  private _loadingProviderContacts$ = new BehaviorSubject<boolean | null>(null);
  private _loadingCallLogs$ = new BehaviorSubject<boolean>(false);
  private _loadingMessageLogs$ = new BehaviorSubject<boolean>(false);
  private _loadingCallSequences$ = new BehaviorSubject<boolean>(false);

  get patientContacts$(): Observable<ContactInformation[]> {
    return this._patientContacts$.asObservable();
  }

  get providerContacts$(): Observable<ContactInformation[]> {
    return this._providerContacts$.asObservable();
  }

  get callState$(): Observable<CallState> {
    return this._callState$.asObservable();
  }

  get phoneNumberCallStates$(): Observable<CallStatesResponse> {
    return this._phoneNumberCallStates$.asObservable();
  }

  get calls$(): Observable<Call[] | null> {
    return this._calls$.asObservable();
  }

  get messages$(): Observable<Message[] | null> {
    return this._messages$.asObservable();
  }

  get editedPatientContact$(): Observable<ContactInformation | null> {
    return this._editedPatientContact$.asObservable();
  }

  get editedProviderContact$(): Observable<ContactInformation | null> {
    return this._editedProviderContact$.asObservable();
  }
  get loadingProviderContacts$(): Observable<boolean | null> {
    return this._loadingProviderContacts$.asObservable();
  }

  public setLoadingProviderContacts(value: boolean): void {
    this._loadingProviderContacts$.next(value);
  }

  get loadingPatientContacts$(): Observable<boolean | null> {
    return this._loadingPatientContacts$.asObservable();
  }

  get loadingCallLogs$(): Observable<boolean> {
    return this._loadingCallLogs$.asObservable();
  }

  get loadingCallSequences$(): Observable<boolean> {
    return this._loadingCallSequences$.asObservable();
  }

  get callSequences$(): Observable<CallSequence[] | null> {
    return this._callSequences$.asObservable();
  }

  get loadingMessageLogs$(): Observable<boolean> {
    return this._loadingMessageLogs$.asObservable();
  }

  get selectedPatientContact$(): Observable<ContactInformation | null> {
    return this._selectedPatientContact$.asObservable();
  }

  get selectedProviderContact$(): Observable<ContactInformation | null> {
    return this._selectedProviderContact$.asObservable();
  }

  private _patientContacts$ = new BehaviorSubject<ContactInformation[]>([]);

  private _providerContacts$ = new BehaviorSubject<ContactInformation[]>([]);

  private _callState$ = new BehaviorSubject<CallState>(CallState.READY);

  private _phoneNumberCallStates$ = new BehaviorSubject<CallStatesResponse>({});

  private _calls$ = new BehaviorSubject<Call[] | null>(null);

  private _messages$ = new BehaviorSubject<Message[] | null>(null);

  private _callSequences$ = new BehaviorSubject<CallSequence[] | null>(null);

  private _editedPatientContact$ = new BehaviorSubject<ContactInformation | null>(null);

  private _editedProviderContact$ = new BehaviorSubject<ContactInformation | null>(null);

  private _selectedPatientContact$ = new BehaviorSubject<ContactInformation | null>(null);

  private _selectedProviderContact$ = new BehaviorSubject<ContactInformation | null>(null);

  private noContactsSnackBarRef?: any;
  private _hasPatientContacts = false;
  private _hasCallbackLocations = false;

  public resetState(): void {
    this._patientContacts$.next([]);
    this._providerContacts$.next([]);
    this._callState$.next(CallState.READY);
    this._phoneNumberCallStates$.next({});
    this._calls$.next(null);
    this._messages$.next(null);
    this._callSequences$.next(null);
    this._loadingCallLogs$.next(false);
    this._loadingMessageLogs$.next(false);
    this._loadingCallSequences$.next(false);
    this._editedPatientContact$.next(null);
    this._editedProviderContact$.next(null);
    this._selectedPatientContact$.next(null);
    this._selectedProviderContact$.next(null);
    this._hasPatientContacts = false;
    this._hasCallbackLocations = false;
  }

  private static addContactToSubject(
    contactInfo: ContactInformation,
    subject: BehaviorSubject<ContactInformation[]>,
  ): void {
    let found = false;
    const contacts = subject.getValue().map((info) => {
      if (contactInfo.id === info.id) {
        found = true;
        return contactInfo;
      }

      return info;
    });

    if (!found) {
      contacts.push(contactInfo);
    }

    subject.next(contacts);
  }

  private static transformResponseToCallState(sequence: Call[]): CallState {
    if (!CollectionUtils.hasValue(sequence)) {
      return CallState.READY;
    }

    switch (sequence[0].status) {
      case 'Attempting':
        return CallState.INITIATED;
      case 'Complete':
        return CallState.COMPLETE;
      case 'Failed':
        return CallState.ERROR;
    }

    return CallState.ERROR;
  }

  private static sort(sequence: CallSequence): CallSequence {
    sequence.callLog.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    sequence.messageLog.sort((a, b) => +b.dateSent - +a.dateSent);

    return sequence;
  }

  private static prependNumberPrefix(contactNumber: string): string {
    return contactNumber.charAt(0) === '+' ? contactNumber : `+1${contactNumber}`;
  }

  private static buildNotificationRequest(
    patientContact: ContactInformation,
    providerContact: ContactInformation,
  ): NotificationRequest {
    return {
      patientId: patientContact.entityId,
      patientContact,
      providerId: providerContact.entityId,
      providerContact,
    };
  }

  constructor(
    private notificationService: NotificationService,
    private contactService: ContactService,
    private snackBar: MatSnackBar,
    private contactsViewModel: ContactsViewModel,
  ) {}

  public initiateNotification() {
    this.updateCallStateByStatus(CallState.INITIATED);
  }

  public resetNotification() {
    this.updateCallStateByStatus(CallState.READY);
  }

  public cancelAlert(
    patientId: string,
    contactNumber: string,
    callSequenceId?: string,
  ): Observable<NotificationCancelResponse> {
    const normalized = NotificationViewModel.prependNumberPrefix(contactNumber);

    return this.notificationService
      .cancelAlert({
        patientId,
        contactNumber: normalized,
        ...(callSequenceId?.trim() ? { callSequenceId: callSequenceId.trim() } : {}),
      })
      .pipe(
        tap(() => this.resetNotification()),
        mergeMap((response) =>
          this.refreshCallSequences(patientId).pipe(
            mergeMap(() => this.refreshPhoneNumberCallStates(patientId)),
            map(() => response),
          ),
        ),
        catchError((err) => this.handleNotificationError(err)),
      );
  }

  public notify(patientId: string): Observable<any> {
    const patientContact = this._selectedPatientContact$.getValue();

    if (!patientContact) of();

    return this.selectedProviderContact$.pipe(
      filter((contact) => !!contact),
      mergeMap((contact) =>
        of(
          NotificationViewModel.buildNotificationRequest(patientContact!, contact!),
        ),
      ),
      tap((request) => this.setupRecurringStatusChecks(request)),
      mergeMap((request) => this.notificationService.notify(request)),
      tap((seq) => this._calls$.next(seq.callLog)),
      tap((seq) => this._messages$.next(seq.messageLog)),
      tap(() => this.refreshCallSequences(patientId).pipe(take(1)).subscribe()),
      // update all cal states after notifying the contact
      tap(() => this.refreshPhoneNumberCallStates(patientId).pipe(take(1)).subscribe()),
      map((seq) => NotificationViewModel.sort(seq)),
      tap((seq) =>
        this._callState$.next(NotificationViewModel.transformResponseToCallState(seq.callLog)),
      ),
      catchError((err) => this.handleNotificationError(err)),
    );
  }

  private setupRecurringStatusChecks(request: NotificationRequest): void {
    if (!request.patientId) return;

    setTimeout(() => {
      this.refreshMessageLogs(request.patientId!)
        .pipe(take(1))
        .subscribe(() => {});

      this.refreshCallLogs(request.patientId!)
        .pipe(take(1))
        .subscribe(() => {});

      this.refreshCallSequences(request.patientId!)
        .pipe(take(1))
        .subscribe(() => {});
    }, 60000);
  }

  private handleNotificationError(err: Error): Observable<never> {
    this.resetNotification();
    return throwError(() => err);
  }

  public refreshCallLogs(patientId: string): Observable<void> {
    if (!patientId) {
      return of(undefined);
    }

    this._loadingCallLogs$.next(true);
    this._calls$.next(null);

    return this.notificationService.callLogs(patientId).pipe(
      map((calls) => calls.sort((a, b) => +a.created - +b.created)),
      tap((calls) => this.updateCallStatus(calls)),
      tap((calls) => this._calls$.next(calls)),
      map(() => undefined),
      finalize(() => this._loadingCallLogs$.next(false)),
    );
  }

  private updateCallStateByStatus(status: string): void {
    switch (status.toLowerCase()) {
      case 'completed':
        this._callState$.next(CallState.COMPLETE);
        break;
      case 'failed':
        this._callState$.next(CallState.ERROR);
        break;
      case 'in-progress':
        this._callState$.next(CallState.ONGOING);
        break;
      case 'ready':
        this._callState$.next(CallState.READY);
        break;
      default:
        this._callState$.next(CallState.INITIATED);
    }
  }

  private updateCallStatus(calls: Call[]): void {
    if (calls.length > 0) {
      this.updateCallStateByStatus(calls[calls.length - 1].status);
    } else {
      this.updateCallStateByStatus(CallState.READY);
    }
  }

  public refreshCallSequences(patientId: string): Observable<void> {
    if (!patientId) {
      return of(undefined);
    }

    this._loadingCallSequences$.next(true);
    this._callSequences$.next(null);

    return this.notificationService.callSequences(patientId).pipe(
      tap((sequences) => this._callSequences$.next(sequences)),
      map(() => undefined),
      finalize(() => this._loadingCallSequences$.next(false)),
    );
  }

  public refreshMessageLogs(patientId: string): Observable<void> {
    if (!patientId) {
      return of(undefined);
    }

    this._loadingMessageLogs$.next(true);
    this._messages$.next(null);

    return this.notificationService.messageLogs(patientId).pipe(
      map((messages) => messages.sort((a, b) => +a.dateSent - +b.dateSent)),
      tap((messages) => this._messages$.next(messages)),
      map(() => undefined),
      finalize(() => this._loadingMessageLogs$.next(false)),
    );
  }

  public refreshPhoneNumberCallStates(patientId: string): Observable<any> {
    return this.notificationService.callState(patientId).pipe(
      tap((states) => {
        this._phoneNumberCallStates$.next(states as any);
      }),
    );
  }

  private async mergeContacts(
    fhirContacts: ContactInformation[],
    dbContacts: ContactInformation[],
  ): Promise<ContactInformation[]> {
    const updatedFHIRContacts: ContactInformation[] = [];

    for (const fhirContact of fhirContacts) {
      if (fhirContact.isPatient) {
        const contact = dbContacts.find(
          (c) => c.contactNumber === fhirContact.contactNumber && c.fromEpic,
        );
        const updateContact = contact ? { ...contact, ...fhirContact } : fhirContact;
        if (
          !contact ||
          contact.name !== fhirContact.name ||
          contact.contactNumber !== fhirContact.contactNumber ||
          contact.emergencyContactName !== fhirContact.emergencyContactName
        ) {
          const saved = await lastValueFrom(
            this.contactService.savePatientContactInformation(updateContact).pipe(take(1)),
          );
          updatedFHIRContacts.push(saved);
        } else {
          updatedFHIRContacts.push(updateContact);
        }
      } else {
        const contact = dbContacts.find(
          (c) =>
            c.fromEpic &&
            (c.locationId === fhirContact.locationId || c.entityId === fhirContact.entityId),
        );
        const updateContact = contact ? { ...contact, ...fhirContact } : fhirContact;
        if (
          !contact ||
          contact.name !== fhirContact.name ||
          contact.contactNumber !== fhirContact.contactNumber
        ) {
          const saved = await lastValueFrom(
            this.contactService.saveProviderContactInformation(updateContact).pipe(take(1)),
          );
          updatedFHIRContacts.push(saved);
        } else {
          updatedFHIRContacts.push(updateContact);
        }
      }
    }

    return [...updatedFHIRContacts, ...dbContacts.filter((c) => !c.fromEpic)];
  }

  public showNoContactsSnackBar(hasContacts: boolean) {
    if (!this.noContactsSnackBarRef && !hasContacts) {
      this.noContactsSnackBarRef = this.snackBar.open(
        Messages.NO_PROVIDER_OR_EMERGENCY_CONTACT,
        'Okay',
      );
      this.noContactsSnackBarRef
        .afterDismissed()
        .pipe(take(1))
        .subscribe(() => {
          this.noContactsSnackBarRef = undefined;
        });
    }
  }

  private evaluateContactsSnackBar(): void {
    if (this._hasPatientContacts || this._hasCallbackLocations) {
      return;
    }
    this.showNoContactsSnackBar(false);
  }

  public markCallbackLocationsUnavailable(): void {
    this._hasCallbackLocations = false;
    this.evaluateContactsSnackBar();
    this._loadingProviderContacts$.next(false);
  }

  public async loadCallbackLocations(
    locations: LocationProvider[],
    defaultLocationId?: string,
  ): Promise<void> {
    this._loadingProviderContacts$.next(true);
    const userId = await this.fhirService.getUserId();
    const contacts: ContactInformation[] = [];

    for (const location of locations) {
      if (!location.id || !location.phone) {
        continue;
      }

      const seed = locationToProviderContact(location);

      try {
        const dbContacts = await lastValueFrom(
          this.contactService.listProviderContactInformation(location.id).pipe(take(1)),
        );
        const merged = await this.mergeContacts(
          [seed],
          dbContacts.filter((c) => (c.userId && c.userId === userId) || c.fromEpic),
        );
        const primary =
          merged.find((c) => c.locationId === location.id && c.fromEpic) ??
          merged.find((c) => c.locationId === location.id) ??
          merged[0];
        if (primary) {
          contacts.push(primary);
        }
      } catch (err) {
        console.error('Error loading callback location contacts', err);
        const merged = await this.mergeContacts([seed], []);
        const primary = merged.find((c) => c.fromEpic) ?? merged[0];
        if (primary) {
          contacts.push(primary);
        }
      }
    }

    this._hasCallbackLocations = contacts.length > 0;
    this.evaluateContactsSnackBar();
    this._providerContacts$.next(contacts);
    this._loadingProviderContacts$.next(false);

    const defaultContact = defaultLocationId
      ? contacts.find(
          (c) => c.locationId === defaultLocationId || c.entityId === defaultLocationId,
        )
      : undefined;
    this.selectProviderContact(defaultContact ?? null);
  }

  public refreshPatientContacts(
    patientId: string,
    fhirContacts: ContactInformation[],
  ): Observable<Promise<void>> {
    this._loadingPatientContacts$.next(true);
    return this.contactService.listPatientContactInformation(patientId).pipe(
      map(async (contacts: ContactInformation[]) => {
        const mergedContacts = await this.mergeContacts(fhirContacts, contacts);
        const hasContacts: boolean = CollectionUtils.hasValue(mergedContacts);
        this._hasPatientContacts = hasContacts;
        this.evaluateContactsSnackBar();
        this._patientContacts$.next(mergedContacts);
        this._loadingPatientContacts$.next(false);
        return this.selectPatientContact(hasContacts ? mergedContacts[0] : null);
      }),
    );
  }

  public refreshProviderContacts(
    providerId: string,
    fhirContacts: ContactInformation[],
  ): Observable<Promise<void>> {
    return this.contactService.listProviderContactInformation(providerId).pipe(
      map(async (contacts: ContactInformation[]) => {
        const userId = await this.fhirService.getUserId();
        const mergedContacts = await this.mergeContacts(
          fhirContacts,
          contacts.filter((c) => (c.userId && c.userId === userId) || c.fromEpic),
        );
        const hasContacts: boolean = CollectionUtils.hasValue(mergedContacts);
        this._hasCallbackLocations = hasContacts;
        this.evaluateContactsSnackBar();
        this._providerContacts$.next(mergedContacts);
        this._loadingProviderContacts$.next(false);
        return this.selectProviderContact(hasContacts ? mergedContacts[0] : null);
      }),
    );
  }

  public saveProviderContact(): Observable<void> {
    return this._editedProviderContact$.pipe(
      filter((info) => !!info),
      tap(
        (info) =>
          (info!.contactNumber = NotificationViewModel.prependNumberPrefix(info!.contactNumber)),
      ),
      mergeMap((info) => this.contactService.saveProviderContactInformation(info!)),
      tap((info) => NotificationViewModel.addContactToSubject(info, this._providerContacts$)),
      map((info) => this.checkForSelectedProviderChanges(info, false)),
      map(() => this.editProviderContact(null)),
    );
  }

  public savePatientContact(): Observable<void> {
    return this._editedPatientContact$.pipe(
      filter((info) => !!info),
      tap(
        (info) =>
          (info!.contactNumber = NotificationViewModel.prependNumberPrefix(info!.contactNumber)),
      ),
      mergeMap((info) => this.contactService.savePatientContactInformation(info!)),
      tap((info) => NotificationViewModel.addContactToSubject(info, this._patientContacts$)),
      map((info) => this.checkForSelectedPatientChanges(info, false)),
      map(() => this.editPatientContact(null)),
    );
  }

  private checkForSelectedPatientChanges(
    contactInformation: ContactInformation,
    isDeleted: boolean,
  ): void {
    const selectedPatient = this._selectedPatientContact$.getValue();

    if (selectedPatient) {
      if (selectedPatient.id === contactInformation.id) {
        const contacts = this._patientContacts$.getValue();
        let contact: ContactInformation | null = contactInformation;

        if (isDeleted) {
          contact = contacts.length > 0 ? contacts[0] : null;
        }

        this.selectPatientContact(contact);
      }
    } else {
      this.selectPatientContact(contactInformation);
    }
  }

  private checkForSelectedProviderChanges(
    contactInformation: ContactInformation,
    isDeleted: boolean,
  ): void {
    const selectedProvider = this._selectedProviderContact$.getValue();

    if (selectedProvider) {
      if (selectedProvider.id === contactInformation.id) {
        const contacts = this._providerContacts$.getValue();
        let contact: ContactInformation | null = contactInformation;

        if (isDeleted) {
          contact = contacts.length > 0 ? contacts[0] : null;
        }

        this.selectProviderContact(contact);
      }
    } else {
      this.selectProviderContact(contactInformation);
    }
  }

  public selectPatientContact(contactInformation: ContactInformation | null): void {
    this._selectedPatientContact$.next(contactInformation);
  }

  public selectProviderContact(contactInformation: ContactInformation | null): void {
    this._selectedProviderContact$.next(contactInformation);
  }

  public editSelectedPatientContact(): void {
    this.editPatientContact(this._selectedPatientContact$.getValue());
  }

  public editSelectedProviderContact(): void {
    this.editProviderContact(this._selectedProviderContact$.getValue());
  }

  public editPatientContact(contactInformation: ContactInformation | null): void {
    if (contactInformation && this._editedPatientContact$.getValue()?.id) {
      contactInformation.id = this._editedPatientContact$.getValue()?.id;
    }

    this._editedPatientContact$.next(contactInformation);
  }

  public editProviderContact(contactInformation: ContactInformation | null): void {
    if (contactInformation && this._editedProviderContact$.getValue()?.id) {
      contactInformation.id = this._editedProviderContact$.getValue()?.id;
    }

    this._editedProviderContact$.next(contactInformation);
  }

  public isProviderSelected(): boolean {
    return !!this._selectedProviderContact$.getValue();
  }

  public isPatientSelected(): boolean {
    return !!this._selectedPatientContact$.getValue();
  }

  public deletePatientContact(contact: ContactInformation): Observable<void> {
    if (!contact.id) return of();

    return this.contactService.deletePatientContactInformation(contact.id).pipe(
      tap(() => this.removeContact(contact.id!, this._patientContacts$)),
      map(() => this.checkForSelectedPatientChanges(contact, true)),
    );
  }

  public deleteProviderContact(contact: ContactInformation): Observable<void> {
    if (!contact.id) return of();
    return this.contactService.deleteProviderContactInformation(contact.id).pipe(
      tap(() => this.removeContact(contact.id!, this._providerContacts$)),
      map(() => this.checkForSelectedProviderChanges(contact, true)),
    );
  }

  private removeContact(id: string, contacts: BehaviorSubject<ContactInformation[]>): void {
    const arr = contacts.getValue();

    if (!CollectionUtils.hasValue(arr)) {
      return;
    }

    contacts.next(arr.filter((contact) => contact.id !== id));
  }

  public isOngoing$(): Observable<boolean> {
    return this.callState$.pipe(map((state) => state === CallState.INITIATED));
  }
}
